import type {Header867, ItemLoop867, NameLoop867, Parsed867} from './parser867';
import {entriesOf} from './sections';
import type {Section, SectionGroup, SectionEntry} from './sections';

type CommonSectionKey867 =
  | 'reportDetails'
  | 'dates'
  | 'parties'
  | 'lines'
  | 'totals';

const SECTION_ORDER_867: CommonSectionKey867[] = [
  'reportDetails',
  'dates',
  'parties',
  'lines',
  'totals',
];

type HeaderKey867 = keyof Header867;

const HEADER_SECTION_867: Record<HeaderKey867, CommonSectionKey867> = {
  bpt: 'reportDetails',
  cur: 'reportDetails',
  refs: 'reportDetails',
  other: 'reportDetails',
  dtm: 'dates',
};

const SECTION_META_867: Record<
  CommonSectionKey867,
  {title: string; description: string}
> = {
  reportDetails: {
    title: 'Report details',
    description:
      'Transaction set purpose, report type, currency and reference numbers.',
  },
  dates: {
    title: 'Dates',
    description:
      'The report period covered by this product transfer and resale report.',
  },
  parties: {
    title: 'Parties & addresses',
    description: 'Who is involved and where.',
  },
  lines: {
    title: 'Transfer & resale lines',
    description:
      'Each product transfer or resale line, its quantity, amounts, pricing and dates.',
  },
  totals: {
    title: 'Totals',
    description: 'Line-item count and report totals.',
  },
};

const NAME_LOOP_KEYS_867: ReadonlyArray<keyof NameLoop867> = [
  'n1',
  'n2',
  'n3',
  'n4',
  'n5',
  'n6',
  'n9',
  'refs',
  'per',
  'other',
];

const ITEM_KEYS_867: ReadonlyArray<keyof ItemLoop867> = [
  'ptd',
  'qty',
  'amt',
  'ctp',
  'refs',
  'dtm',
  'other',
];

export function buildSections867(parsed: Parsed867, txPath: string): Section[] {
  const flat: Record<CommonSectionKey867, SectionEntry[]> = {
    reportDetails: [],
    dates: [],
    parties: [],
    lines: [],
    totals: [],
  };
  const headerKeys = Object.keys(HEADER_SECTION_867) as HeaderKey867[];
  for (const key of headerKeys) {
    flat[HEADER_SECTION_867[key]].push(
      ...entriesOf(parsed.header, [key], `${txPath}.header`)
    );
  }
  flat.totals.push(
    ...entriesOf(parsed.summary, ['ctt', 'amt', 'other'], `${txPath}.summary`)
  );

  const partyGroups: SectionGroup[] = parsed.nameLoops.map((loop, i) => {
    const path = `${txPath}.nameLoops[${i}]`;
    return {
      key: path,
      entries: entriesOf(loop, NAME_LOOP_KEYS_867, path),
    };
  });

  const lineGroups: SectionGroup[] = parsed.items.map((item, i) => {
    const path = `${txPath}.items[${i}]`;
    return {
      key: path,
      entries: entriesOf(item, ITEM_KEYS_867, path),
    };
  });

  return SECTION_ORDER_867.flatMap((key): Section[] => {
    const repeating = key === 'parties' || key === 'lines';
    const groups = repeating
      ? key === 'parties'
        ? partyGroups
        : lineGroups
      : flat[key].length > 0
        ? [{key, entries: flat[key]}]
        : [];
    if (groups.length === 0) return [];
    return [{key, ...SECTION_META_867[key], repeating, groups}];
  });
}
